import { makeAutoObservable, runInAction } from 'mobx';
import { AxiosInstance } from 'axios';
import { AxiosResponseHelper } from 'utilities/AxiosResponseHelper';
import { t } from 'utilities/TranslationUtils';
import {
  SessionsPageDto, PlayerOptionDto, PlayerKindStatsDto, MeasurementMarkerDto, SessionOptionDto, SessionKindGroupName, SnapshotMetaDto, FreeKickZoneDto, CornerZoneLayoutDto, CornerSide,
} from 'types/models/SessionsPageData';
import {
  KIND_FILTER, buildOnTargetTrendPoints, DEFAULT_TIMEFRAME, Timeframe,
} from 'pages/PlayerPerformance/playerPerformanceUtils';
import { IResult } from 'services/Result';
import type { OnTargetTrendPoint } from 'pages/PlayerPerformance/OnTargetTrend';

export type VideoLite = { videoId?: string; url?: string; label?: string; thumbnailUrl?: string };

export type PlayerPerformanceUsageAction = 'MainPageOpened' | 'PlayerOpened' | 'SessionTypeSelected';

export interface PlayerPerformanceUsageEventRequest {
  action: PlayerPerformanceUsageAction;
  playerId?: string;
  sessionType?: string;
}

export interface SessionDetailsData {
  videos: VideoLite[];
  trajectorySamples: number[][] | null;
}

/** Broad fixed window — the page has no period UI; we load all available data. */
const SELECTION_PERIOD = 'all';

/**
 * Backoff (ms) between poll attempts while a player's heavy snapshot is still generating (HTTP 202).
 * The list length also caps the number of retries — after the last delay we stop and surface an error.
 * ~30s total, matching the "no server backstop timer; the refresh button is the recovery" design.
 */
const PLAYER_POLL_BACKOFF_MS = [1000, 2000, 3000, 5000, 8000, 8000];

const delay = (ms: number): Promise<void> => new Promise((resolve) => { setTimeout(resolve, ms); });

/** Outcome of a player fetch: loaded, still-generating (retryable), or a hard failure. */
type PlayerFetchResult =
  | { kind: 'ok'; dto: SessionsPageDto }
  | { kind: 'not_ready' }
  | { kind: 'error' };

export class PlayerPerformanceStore {
  players: PlayerOptionDto[] = [];
  measurements: MeasurementMarkerDto[] = [];
  freeKickZones: FreeKickZoneDto[] = [];
  cornerZoneLayouts: CornerZoneLayoutDto[] = [];
  sessions: SessionOptionDto[] = [];
  /** Snapshot-level metadata (generation time + org-wide counts) for the header info popover. */
  snapshotMeta: SnapshotMetaDto | null = null;
  isLoading: boolean = false;
  loadError: string | null = null;

  /** True once the "Filter by Team" chip has been activated. */
  teamFilterActive: boolean = false;
  /** Selected `${club}␟${team}` cell, or null while none is chosen. */
  selectedTeamKey: string | null = null;
  /** Gate between selection screen (null) and performance view (set). */
  focusedPlayerId: string | null = null;
  /** Currently active session-type tab filter value (Players-page filter vocabulary). */
  selectedSessionType: string = 'FreeKicks';

  /**
   * Kick details (videos + trajectory) by `"sessionId:measurementId"`, loaded on demand. One entry per kick, so the
   * kick modal, the table's detail panel and the hover popup can each show their own kick at the same time.
   */
  sessionDetailsByKey: Map<string, SessionDetailsData> = new Map();
  private detailRequests: Map<string, Promise<void>> = new Map();

  /** Flight paths ([x, z] samples) by measurement id, filled from whole sessions as they are loaded (see loadSessionTrajectories). */
  trajectoryByMeasurementId: Map<string, number[][]> = new Map();
  /** Sessions whose trajectories were requested (loading or loaded), so each is fetched once. */
  private requestedTrajectorySessions: Set<string> = new Set();

  /** True while a selected player's heavy snapshot is loading (including 202 poll wait). */
  isLoadingPlayer: boolean = false;
  /** Error surfaced when a player's snapshot genuinely fails to load. */
  playerLoadError: string | null = null;
  /** True when the player's snapshot is still generating downstream (202) after the poll budget —
   *  a retryable "not ready yet" state, distinct from a hard failure. */
  playerNotReady: boolean = false;
  /** Player ids whose heavy snapshot (measurements + kindStats) is already in-store — the per-player cache. */
  loadedPlayerIds: Set<string> = new Set();

  /**
   * View preferences that stay "sticky" while switching player / session type (cleared by a full page refresh).
   * Layout and side are only applied where they make sense for the new context — the view validates them.
   */
  timeframe: Timeframe = DEFAULT_TIMEFRAME;
  freeKickLayoutId: string = '1';
  cornerLayoutId: string | null = null;
  cornerSide: CornerSide | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  setTimeframe(timeframe: Timeframe): void { this.timeframe = timeframe; }
  setFreeKickLayoutId(layoutId: string): void { this.freeKickLayoutId = layoutId; }
  setCornerLayoutId(layoutId: string): void { this.cornerLayoutId = layoutId; }
  setCornerSide(side: CornerSide): void { this.cornerSide = side; }

  get focusedPlayer(): PlayerOptionDto | null {
    return this.players.find((p) => p.id === this.focusedPlayerId) ?? null;
  }

  /** Kind derived from selectedSessionType via reverse KIND_FILTER lookup. */
  get activeKind(): SessionKindGroupName {
    const entry = (Object.entries(KIND_FILTER) as [SessionKindGroupName, { filterValue: string }][])
      .find(([, v]) => v.filterValue === this.selectedSessionType);
    return entry?.[0] ?? 'FreeKick';
  }

  /** Measurements for the focused player filtered to the active kind. */
  get playerKindMeasurements(): MeasurementMarkerDto[] {
    if (!this.focusedPlayerId) return [];
    const kind = this.activeKind;
    return this.measurements.filter((m) => m.playerId === this.focusedPlayerId && m.kind === kind);
  }

  /** Per-session on-target trend points for the focused player + active kind. */
  get onTargetTrendPoints(): OnTargetTrendPoint[] {
    return buildOnTargetTrendPoints(this.playerKindMeasurements);
  }

  /** Server-computed stats for the focused player's active kind. */
  get focusedKindStats(): PlayerKindStatsDto | null {
    return this.focusedPlayer?.kindStats?.[this.activeKind] ?? null;
  }

  setTeamFilterActive(active: boolean): void {
    this.teamFilterActive = active;
    if (!active) this.selectedTeamKey = null;
  }

  setSelectedTeamKey(key: string): void {
    this.selectedTeamKey = key;
  }

  clearTeamFilter(): void {
    this.teamFilterActive = false;
    this.selectedTeamKey = null;
  }

  focusPlayer(id: string, sessionTypeFilter: string): void {
    this.focusedPlayerId = id;
    this.selectedSessionType = sessionTypeFilter;
  }

  setSessionType(sessionTypeFilter: string): void {
    this.selectedSessionType = sessionTypeFilter;
  }

  clearFocus(): void {
    this.focusedPlayerId = null;
  }

  /**
   * Loads one session's detail (it carries every kick's flight path) and keeps the paths for the top view. Used for the
   * kicks the user selects, which the snapshot's marker rows carry no trajectories for. Best effort: failures are ignored.
   */
  async loadSessionTrajectories(apiInstance: AxiosInstance, sessionId: string, anyMeasurementId: string): Promise<void> {
    if (this.requestedTrajectorySessions.has(sessionId)) return;
    this.requestedTrajectorySessions.add(sessionId);
    try {
      const params = new URLSearchParams({ sessionId, measurementId: anyMeasurementId });
      const response = await apiInstance.get<IResult<{ measurements?: { measurementId: string; trajectorySamples?: number[][] | null }[] }>>(`api/SessionDetails?${params.toString()}`);
      if (!AxiosResponseHelper.IsSuccessfulResponse(response)) {
        this.requestedTrajectorySessions.delete(sessionId);
        return;
      }
      runInAction(() => {
        (response.data.data?.measurements ?? []).forEach((m) => {
          if (m.trajectorySamples && m.trajectorySamples.length > 1) this.trajectoryByMeasurementId.set(m.measurementId, m.trajectorySamples);
        });
      });
    } catch {
      this.requestedTrajectorySessions.delete(sessionId);
    }
  }

  /** The loaded details of one kick, or null while they are loading / not requested. Read it in an observer to follow loading. */
  sessionDetailsFor(sessionId: string, measurementId: string): SessionDetailsData | null {
    return this.sessionDetailsByKey.get(`${sessionId}:${measurementId}`) ?? null;
  }

  /** Loads one kick's details once; concurrent callers for the same kick share the request. A failure caches "no videos". */
  loadSessionDetails(apiInstance: AxiosInstance, sessionId: string, measurementId: string): Promise<void> {
    const key = `${sessionId}:${measurementId}`;
    if (this.sessionDetailsByKey.has(key)) return Promise.resolve();
    const inFlight = this.detailRequests.get(key);
    if (inFlight) return inFlight;

    const empty: SessionDetailsData = { videos: [], trajectorySamples: null };
    const request = (async () => {
      let details = empty;
      try {
        const params = new URLSearchParams({ sessionId, measurementId });
        const response = await apiInstance.get<IResult<{ measurement?: { videos?: VideoLite[]; trajectorySamples?: number[][] | null } }>>(`api/SessionDetails?${params.toString()}`);
        if (AxiosResponseHelper.IsSuccessfulResponse(response)) {
          const m = response.data.data?.measurement;
          details = {
            videos: (m?.videos ?? []).filter((v) => Boolean(v.url && v.url.length > 0)),
            trajectorySamples: m?.trajectorySamples ?? null,
          };
        }
      } catch {
        // Details are an enhancement of the kick views: show them without videos rather than fail.
      }
      runInAction(() => { this.sessionDetailsByKey.set(key, details); });
      this.detailRequests.delete(key);
    })();
    this.detailRequests.set(key, request);
    return request;
  }

  async LoadSelectionData(apiInstance: AxiosInstance, opts?: { bypassCache?: boolean; refresh?: boolean }): Promise<void> {
    runInAction(() => {
      this.isLoading = true;
      this.loadError = null;
    });
    try {
      // Defaults (cache=true, refresh=false) are omitted so the URL stays clean; only the
      // force-refresh knobs are sent when explicitly requested via ?cache=false / ?refresh=true.
      const params = new URLSearchParams({ period: SELECTION_PERIOD });
      if (opts?.bypassCache) params.set('cache', 'false');
      if (opts?.refresh) params.set('refresh', 'true');
      const response = await apiInstance.get<IResult<SessionsPageDto>>(`api/OrgSnapshot/sessions?${params.toString()}`);
      if (AxiosResponseHelper.IsSuccessfulResponse(response) && response.data.data) {
        const dto = response.data.data;
        // SDK layout ids are numeric strings (e.g. "1", "2", "8").
        // Display names come from translation keys (playerPerformance.cornerLayoutName_<id>);
        // unknown layout ids fall back to the SDK-provided name.
        const renamedLayouts = (dto.cornerZoneLayouts ?? []).map((l) => {
          const nameKey = `playerPerformance.cornerLayoutName_${l.layoutId}`;
          const translated = t(nameKey);
          return { ...l, layoutName: translated !== nameKey ? translated : l.layoutName };
        });

        runInAction(() => {
          this.players = dto.availablePlayers;
          this.measurements = dto.periodMeasurements;
          this.freeKickZones = dto.freeKickZones ?? [];
          this.cornerZoneLayouts = renamedLayouts;
          this.sessions = dto.availableSessions;
          this.snapshotMeta = dto.snapshotMeta ?? null;
        });
      } else {
        runInAction(() => { this.loadError = t('playerPerformance.loadFailed'); });
      }
    } catch {
      runInAction(() => { this.loadError = t('playerPerformance.loadFailed'); });
    } finally {
      runInAction(() => { this.isLoading = false; });
    }
  }

  /**
   * Loads a single player's heavy snapshot (measurements + per-kind stats) from
   * `GET api/OrgSnapshot/player/{playerId}`. Handles a 202 "not ready yet" by polling with backoff,
   * and caches per-player in-store so re-selecting the same player does not refetch (unless a
   * force-refresh is requested). The player's kicks are merged into `measurements` and their
   * kindStats onto the roster entry, so the existing focused-player getters keep working unchanged.
   */
  async LoadPlayerData(
    apiInstance: AxiosInstance,
    playerId: string,
    opts?: { period?: string; kinds?: string; bypassCache?: boolean; refresh?: boolean },
  ): Promise<void> {
    // Cache hit: player already loaded and no force-refresh → nothing to do.
    if (this.loadedPlayerIds.has(playerId) && !opts?.bypassCache && !opts?.refresh) return;

    runInAction(() => {
      this.isLoadingPlayer = true;
      this.playerLoadError = null;
      this.playerNotReady = false;
    });
    try {
      const result = await this.fetchPlayerWithPolling(apiInstance, playerId, opts);
      runInAction(() => {
        if (result.kind === 'ok') {
          this.mergePlayerData(result.dto, playerId);
          this.loadedPlayerIds.add(playerId);
        } else if (result.kind === 'not_ready') {
          // Snapshot is still generating downstream — distinct from a hard failure, and retryable.
          this.playerNotReady = true;
        } else {
          this.playerLoadError = t('playerPerformance.loadFailed');
        }
      });
    } catch {
      runInAction(() => { this.playerLoadError = t('playerPerformance.loadFailed'); });
    } finally {
      runInAction(() => { this.isLoadingPlayer = false; });
    }
  }

  /**
   * Fetches the player payload, retrying while the blob is still generating (HTTP 202). `refresh`
   * is only sent on the first attempt so a poll never re-queues generation. Distinguishes a still-
   * generating snapshot (`not_ready`, retryable) from a genuine failure (`error`).
   */
  private async fetchPlayerWithPolling(
    apiInstance: AxiosInstance,
    playerId: string,
    opts?: { period?: string; kinds?: string; bypassCache?: boolean; refresh?: boolean },
  ): Promise<PlayerFetchResult> {
    for (let attempt = 0; attempt <= PLAYER_POLL_BACKOFF_MS.length; attempt += 1) {
      const params = new URLSearchParams({ period: opts?.period ?? SELECTION_PERIOD });
      if (opts?.kinds) params.set('kinds', opts.kinds);
      if (opts?.bypassCache) params.set('cache', 'false');
      if (opts?.refresh && attempt === 0) params.set('refresh', 'true');

      const response = await apiInstance.get<IResult<SessionsPageDto>>(`api/OrgSnapshot/player/${playerId}?${params.toString()}`);

      // 202 → snapshot still generating. Back off and poll again until the budget is exhausted.
      if (response.status === 202) {
        if (attempt === PLAYER_POLL_BACKOFF_MS.length) return { kind: 'not_ready' };
        await delay(PLAYER_POLL_BACKOFF_MS[attempt]);
        continue;
      }

      if (AxiosResponseHelper.IsSuccessfulResponse(response) && response.data.data) return { kind: 'ok', dto: response.data.data };
      return { kind: 'error' };
    }
    return { kind: 'error' };
  }

  /** Merges one player's payload into the accumulated store state (roster kindStats + measurements). */
  private mergePlayerData(dto: SessionsPageDto, playerId: string): void {
    const option = dto.availablePlayers.find((p) => p.id === playerId) ?? dto.availablePlayers[0] ?? null;
    if (option) {
      // Replace the roster entry in place (preserving order) so the selection screen ranking is stable;
      // append only if the player wasn't already on the lean roster.
      // The player payload has no avatar (only the landing roster does), so keep the one already known.
      const idx = this.players.findIndex((p) => p.id === option.id);
      this.players = idx >= 0
        ? this.players.map((p, i) => (i === idx ? { ...option, avatarUrl: option.avatarUrl ?? p.avatarUrl } : p))
        : [...this.players, option];
    }
    // Replace just this player's kicks so a force-refresh stays consistent; keep other players cached.
    const otherMeasurements = this.measurements.filter((m) => m.playerId !== playerId);
    this.measurements = [...otherMeasurements, ...dto.periodMeasurements];
  }

  trackUsage(apiInstance: AxiosInstance, request: PlayerPerformanceUsageEventRequest): void {
    void apiInstance.post('api/OrgSnapshot/player-performance/usage', request).catch(() => undefined);
  }
}
